import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdFlag, MdPerson, MdDelete, MdExpandMore, MdExpandLess } from 'react-icons/md';

function AdminFlaggedPolls() {
  const [flaggedPolls, setFlaggedPolls] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPoll, setExpandedPoll] = useState(null);
  const [pollDetails, setPollDetails] = useState({});

  useEffect(() => {
    loadFlaggedPolls();
    loadTags();
  }, []);

  const loadFlaggedPolls = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/flagged-polls`);
      setFlaggedPolls(response.data.flaggedPolls || []);
    } catch (error) {
      console.error('Failed to load flagged polls:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const loadPollDetails = async (pollId) => {
    if (pollDetails[pollId]) {
      return; // Already loaded
    }
    try {
      const response = await axios.get(`${API_URL}/api/admin/flagged-polls/${pollId}`);
      setPollDetails(prev => ({
        ...prev,
        [pollId]: response.data
      }));
    } catch (error) {
      console.error('Failed to load poll flag details:', error);
    }
  };

  const handleExpand = async (pollId) => {
    if (expandedPoll === pollId) {
      setExpandedPoll(null);
    } else {
      setExpandedPoll(pollId);
      await loadPollDetails(pollId);
    }
  };

  const handleDismiss = async (pollId) => {
    if (!window.confirm('Are you sure you want to dismiss all flags for this poll?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/flagged-polls/${pollId}/dismiss`);
      loadFlaggedPolls();
      setExpandedPoll(null);
    } catch (error) {
      alert('Failed to dismiss flags');
    }
  };

  const handleDeletePoll = async (pollId) => {
    if (!window.confirm('Are you sure you want to DELETE this poll? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/polls/${pollId}`);
      // Also dismiss the flags
      await axios.delete(`${API_URL}/api/admin/flagged-polls/${pollId}/dismiss`);
      loadFlaggedPolls();
      setExpandedPoll(null);
    } catch (error) {
      alert('Failed to delete poll');
    }
  };

  const getTagName = (tagId) => {
    const tag = tags.find(t => t.tagId === tagId);
    return tag ? tag.name : 'Unknown';
  };

  const getTagColor = (tagId) => {
    const tag = tags.find(t => t.tagId === tagId);
    return tag ? tag.color : '#gray';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header with count */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 font-rubik flex items-center gap-2">
          <MdFlag className="text-danger" />
          Flagged Polls Review
        </h2>
        <span className="text-sm text-gray-600 font-rubik">
          {flaggedPolls.length} poll{flaggedPolls.length !== 1 ? 's' : ''} flagged
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
        </div>
      ) : flaggedPolls.length === 0 ? (
        <div className="card-container text-center py-12">
          <MdFlag className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-rubik">No flagged polls to review</p>
          <p className="text-gray-400 text-sm font-rubik mt-2">All clear! Users have not reported any polls.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {flaggedPolls.map(poll => (
              <div 
                key={poll.pollId} 
                className="card-container"
                data-testid={`flagged-poll-${poll.pollId}`}
              >
                {/* Poll Header */}
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2 font-rubik">{poll.question}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {poll.tags.map(tagId => (
                        <span
                          key={tagId}
                          className="px-3 py-1 rounded-full text-sm font-medium text-white"
                          style={{ backgroundColor: getTagColor(tagId) }}
                        >
                          {getTagName(tagId)}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-danger font-medium flex items-center gap-1 font-rubik">
                        <MdFlag className="text-base" />
                        {poll.flagCount} report{poll.flagCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleExpand(poll.pollId)}
                      className="px-4 py-2 btn-secondary text-sm flex items-center gap-1"
                      data-testid={`expand-flags-${poll.pollId}`}
                    >
                      {expandedPoll === poll.pollId ? (
                        <>
                          <MdExpandLess className="text-lg" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <MdExpandMore className="text-lg" />
                          View Details
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleDismiss(poll.pollId)}
                      className="px-4 py-2 btn-tertiary text-sm"
                      data-testid={`dismiss-flags-${poll.pollId}`}
                    >
                      Dismiss
                    </button>
                    <button
                      onClick={() => handleDeletePoll(poll.pollId)}
                      className="px-4 py-2 btn-danger text-sm flex items-center gap-1"
                      data-testid={`delete-poll-${poll.pollId}`}
                    >
                      <MdDelete className="text-lg" />
                      Delete Poll
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedPoll === poll.pollId && pollDetails[poll.pollId] && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 font-rubik">
                      Reports ({pollDetails[poll.pollId].totalFlags})
                    </h4>
                    <div className="space-y-3">
                      {pollDetails[poll.pollId].flags.map(flag => (
                        <div 
                          key={flag.flagId}
                          className="bg-red-50 border border-red-200 rounded-card p-4"
                          data-testid={`flag-detail-${flag.flagId}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <MdPerson className="text-gray-500" />
                                <span className="font-medium text-gray-800 font-rubik">{flag.username}</span>
                              </div>
                              <p className="text-sm text-gray-700 font-rubik">
                                <span className="font-medium">Reason:</span> {flag.reason}
                              </p>
                            </div>
                            <span className="text-xs text-gray-500 font-rubik">
                              {new Date(flag.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

export default AdminFlaggedPolls;
